import type { CollectionState } from './state';
import type { CollectionOptions } from './use_collection';

/**
 * Build a browser URL from the current pathname and query parameters,
 * omitting the trailing `?` when the query string is empty.
 */
export function buildBrowserURL(qs: URLSearchParams): string {
	const qsString = qs.toString();
	if (qsString.length === 0) {
		return window.location.pathname;
	}
	return `${window.location.pathname}?${qsString}`;
}

/**
 * Set pagination parameters in the browser URL.
 * Note: Sorting parameters are handled by the useSorting hook.
 * @param state
 */
export function updateBrowserURL<T>(state: CollectionState<T>): string {
	const qs = new URLSearchParams(window.location.search);

	if (state.page >= 1) {
		qs.set('page', state.page.toString());
	} else {
		qs.delete('page');
	}

	return buildBrowserURL(qs);
}

/**
 * Build the endpoint URL with the given options and state.
 * This will be the complete endpint path with query parameters.
 * If count, page, or sorting options are present in the options they will be overridden by the state.
 * @param endpoint Endpoint path in the API
 * @param options
 * @param state
 * @param sortBy Optional sort column
 * @param sortByOrder Optional sort order
 * @returns
 */
export function buildEndpointURL<T>(
	endpoint: string,
	options: CollectionOptions<T>,
	state: CollectionState<T>,
	sortBy?: string,
	sortByOrder?: string
): string {
	if (!endpoint) {
		return '';
	}

	const params = new URLSearchParams(options.queryParams ?? {});
	if (options.count) {
		params.set('count', options.count.toString());
	}
	if (state.page > 1) {
		params.set('page', state.page.toString());
	}
	if (sortBy) {
		params.set('sort_by', sortBy);
	}
	if (sortByOrder) {
		params.set('sort_by_order', sortByOrder);
	}
	if (options.actions?.length) {
		params.set('allowed_actions', options.actions.join(','));
	}

	if (Array.from(params.keys()).length === 0) {
		return endpoint;
	}

	return endpoint + '?' + params.toString();
}
